import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AdminApiError,
  getSiteLiftRules,
  setSiteLiftRules,
  type AdminLiftRuleBlock,
  type AdminLiftRulesStatus,
} from '../../api/adminClient';
import './LiftRulesSection.css';

/**
 * A site's lift rules switch (Akash, 2026-11-03). Off by default, and hotel staff
 * then see nothing about lift rules at all.
 *
 * The checks are what the site's fleet adapter reports, so they cannot drift from
 * what is there. The server refuses a turn-on until they pass; the button only says
 * so first. Turning off asks first, naming each block it removes: an admin can
 * always turn it off, and is told what that does.
 */

const POLL_MS = 10_000;

function ageWords(seconds: number): string {
  if (seconds < 60) return `${seconds} s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${hours} h ago` : `${Math.floor(hours / 24)} d ago`;
}

function Check({
  ok,
  children,
  testId,
}: {
  ok: boolean;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <li className="nx-lift-switch__check" data-testid={testId} data-ok={ok}>
      <span className={ok ? 'nx-lift-switch__yes' : 'nx-lift-switch__no'} aria-label={ok ? 'Done' : 'Not done'}>
        {ok ? '✓' : '✕'}
      </span>
      <span>{children}</span>
    </li>
  );
}

function Quiet({ children }: { children: React.ReactNode }) {
  return <span className="nx-lift-switch__quiet"> · {children}</span>;
}

function Checks({ status }: { status: AdminLiftRulesStatus }) {
  const { heard_age_s: heard, reporting, remote_intent: remote, lifts } = status;
  const notKnown = <Quiet>not known until it reports</Quiet>;
  return (
    <ul className="nx-lift-switch__checks">
      <Check ok={reporting} testId="lift-rules-check-reporting">
        Fleet adapter is reporting its lifts
        <Quiet>
          {heard === null
            ? 'nothing heard yet'
            : `${reporting ? 'heard' : 'last heard'} ${ageWords(heard)}`}
        </Quiet>
      </Check>
      <Check ok={remote} testId="lift-rules-check-remote">
        Site accepts rules from the console
        {!reporting ? (
          notKnown
        ) : remote ? null : (
          <Quiet>
            <code>remote_intent</code> is off
          </Quiet>
        )}
      </Check>
      <Check ok={lifts.length > 0} testId="lift-rules-check-lifts">
        {!reporting ? (
          <>
            Lifts set up for robots
            {notKnown}
          </>
        ) : lifts.length === 0 ? (
          'No lifts set up for robots'
        ) : (
          <>
            {lifts.length === 1 ? '1 lift' : `${lifts.length} lifts`} set up for robots
            <Quiet>{lifts.join(', ')}</Quiet>
          </>
        )}
      </Check>
    </ul>
  );
}

function blockWords(block: AdminLiftRuleBlock): string {
  const what =
    block.scope === 'between_floors'
      ? `Travel between floors on ${block.lift} is blocked`
      : `${block.lift} is blocked from robot use`;
  const until =
    block.until_kind === 'time' && block.until_ms
      ? `until ${new Date(block.until_ms).toLocaleString()}`
      : block.until_kind === 'next_change'
        ? 'until the next calendar change'
        : 'until released';
  const reason = block.reason ? ` (${block.reason})` : '';
  const again =
    block.scope === 'between_floors'
      ? `robots may travel between floors on ${block.lift} again`
      : `robots may use ${block.lift} again`;
  return `${what} ${until}${reason}. Turning this off removes that block, and ${again}.`;
}

function calendarWords(count: number): string {
  if (count === 0) return 'No calendar rules are stored.';
  return count === 1
    ? '1 calendar rule is kept, and comes back if you turn it on again.'
    : `${count} calendar rules are kept, and come back if you turn it on again.`;
}

export function LiftRulesSection({ siteId, archived }: { siteId: string; archived: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admin', 'site-lift-rules', siteId],
    queryFn: () => getSiteLiftRules(siteId),
    // The checks are live: an adapter may come up while someone is looking.
    refetchInterval: POLL_MS,
  });
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: ({ enabled, confirm }: { enabled: boolean; confirm: boolean }) =>
      setSiteLiftRules(siteId, enabled, confirm),
    onSuccess: () => {
      setError(null);
      setAsking(false);
    },
    onError: (err) =>
      setError(err instanceof AdminApiError ? err.message : 'Could not change it. Nothing changed.'),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'site-lift-rules', siteId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'site-settings', siteId] });
    },
  });

  const status = q.data;
  return (
    <>
      <h4 className="nx-lift-switch__heading">Lift rules</h4>
      {q.isPending && <p className="nx-lift-switch__muted">Loading…</p>}
      {q.isError && !status && (
        <p className="nx-lift-switch__warn" data-testid="lift-rules-load-error">
          Could not read this site&apos;s lift rules. This is a separate request: nothing above is
          affected, and the site keeps running as it is.
        </p>
      )}
      {status && (
        <section className="nx-lift-switch" data-testid="lift-rules-section">
          <div className="nx-lift-switch__top">
            <strong>Lift rules</strong>
            <span
              className={`nx-lift-switch__state${status.enabled ? ' is-on' : ''}`}
              data-testid="lift-rules-state"
            >
              <span className="nx-lift-switch__glyph" aria-hidden="true" />
              {status.enabled ? 'On' : 'Off'}
            </span>
          </div>
          <p className="nx-lift-switch__muted">
            Lets supervisors block a lift from robots and allow travel between floors, by hand or by
            calendar. While it is off, hotel staff see nothing about it.
          </p>
          <Checks status={status} />

          {!status.enabled && !archived && (
            <div className="nx-lift-switch__actions">
              <button
                type="button"
                disabled={!status.ready || change.isPending}
                onClick={() => change.mutate({ enabled: true, confirm: false })}
                data-testid="lift-rules-turn-on"
              >
                {status.ready ? 'Turn on' : 'Turn on (finish the setup first)'}
              </button>
            </div>
          )}

          {status.enabled && !archived && !asking && (
            <div className="nx-lift-switch__actions">
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setAsking(true);
                }}
                data-testid="lift-rules-turn-off"
              >
                Turn off…
              </button>
            </div>
          )}

          {status.enabled && !archived && asking && (
            <div className="nx-lift-switch__ask" role="group" aria-label="Confirm turning lift rules off" data-testid="lift-rules-off-ask">
              {status.blocks.map((block) => (
                <p key={`${block.lift}:${block.scope}`} data-testid="lift-rules-off-block">
                  {blockWords(block)}
                </p>
              ))}
              {status.blocks.length === 0 && <p>No lift is blocked just now.</p>}
              <p className="nx-lift-switch__muted">{calendarWords(status.calendar_rules)}</p>
              <div className="nx-lift-switch__actions">
                <button type="button" onClick={() => setAsking(false)} data-testid="lift-rules-keep-on">
                  Keep it on
                </button>
                <button
                  type="button"
                  className="nx-lift-switch__go"
                  disabled={change.isPending}
                  onClick={() => change.mutate({ enabled: false, confirm: true })}
                  data-testid="lift-rules-off-confirm"
                >
                  {status.blocks.length === 0
                    ? 'Turn off'
                    : status.blocks.length === 1
                      ? 'Turn off and remove the block'
                      : `Turn off and remove ${status.blocks.length} blocks`}
                </button>
              </div>
            </div>
          )}

          {error && (
            <p className="nx-lift-switch__error" role="alert" data-testid="lift-rules-error">
              {error}
            </p>
          )}
          {archived && (
            <p className="nx-lift-switch__muted">
              Decommissioned: re-commission the site to change its settings.
            </p>
          )}
        </section>
      )}
    </>
  );
}
